/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {normalizeAsset} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  consolidatedAssetUpdate,
  createAsset,
  createAssetDevices,
  deleteAsset as destroyAsset,
  deleteAssetDevices,
  deleteAssets as destroyAssets,
  deleteAssetSchedule,
  disassociateDevices,
  getAsset,
  loadAssets,
  manageAssetSchedules,
  sendStarterCommand,
  updateAsset,
  updateAssetSchedule,
  updateAssetSchedules,
  updateAssetToolAssociations,
  updateBulkAssetMaintenanceSchedules,
  updateBulkAssetSchedules,
  updateDevice
} from '@ion/core/src/services/Connect';
import {entityId, hasSameAccount} from '@ion/core/src/services/ConnectUtils';
import {
  assignSchedule,
  deleteAssetMaintenanceSchedule
} from '@ion/core/src/services/Maintenance';
import {
  Account,
  Asset,
  Device,
  Entity,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {GenericFunction} from '@ion/core/src/types/general';
import {LoadAssetsParams} from '@ion/core/src/types/services';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {importConfirm} from './importer';
import {toggleUserPrompt} from './user-prompt';

// name
export const NAME                         = 'adminAssets';

// initialize assets ...
export const INITIALIZE_ADMIN_ASSETS      = 'INITIALIZE_ADMIN_ASSETS';
export const RE_INITIALIZE_ADMIN_ASSETS   = 'RE_INITIALIZE_ADMIN_ASSETS';

// refresh grid ...
export const ADMIN_ASSETS_GRID_REFRESHED  = 'ADMIN_ASSETS_GRID_REFRESHED';

export const SET_GRID_COLUMNS             = 'SET_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS          = 'UPDATE_GRID_COLUMNS';

// retrieve assets ...
export const GET_ADMIN_ASSETS_REQUEST     = 'GET_ADMIN_ASSETS_REQUEST';
export const GET_ADMIN_ASSETS_RESPONSE    = 'GET_ADMIN_ASSETS_RESPONSE';
export const GET_ADMIN_ASSETS_ERROR       = 'GET_ADMIN_ASSETS_ERROR';

// asset add ...
export const ADD_ADMIN_ASSET              = 'ADD_ADMIN_ASSET';
export const ADD_ADMIN_ASSET_REQUEST      = 'ADD_ADMIN_ASSET_REQUEST';
export const ADD_ADMIN_ASSET_RESPONSE     = 'ADD_ADMIN_ASSET_RESPONSE';
export const ADD_ADMIN_ASSET_ERROR        = 'ADD_ADMIN_ASSET_ERROR';

// asset bulk import
export const IMPORT_ADMIN_ASSETS          = 'IMPORT_ADMIN_ASSETS';

// asset edit ...
export const EDIT_ADMIN_ASSET             = 'EDIT_ADMIN_ASSET';
export const EDIT_ADMIN_ASSET_REQUEST     = 'EDIT_ADMIN_ASSET_REQUEST';
export const EDIT_ADMIN_ASSET_RESPONSE    = 'EDIT_ADMIN_ASSET_RESPONSE';
export const EDIT_ADMIN_ASSET_ERROR       = 'EDIT_ADMIN_ASSET_ERROR';

// manage asset associations ...
export const MANAGE_ADMIN_ASSET           = 'MANAGE_ADMIN_ASSET';
export const MANAGE_ADMIN_ASSET_REQUEST   = 'MANAGE_ADMIN_ASSET_REQUEST';
export const MANAGE_ADMIN_ASSET_RESPONSE  = 'MANAGE_ADMIN_ASSET_RESPONSE';
export const MANAGE_ADMIN_ASSET_ERROR     = 'MANAGE_ADMIN_ASSET_ERROR';

// update asset associations ...
export const UPDATE_ADMIN_ASSET           = 'UPDATE_ADMIN_ASSET';
export const UPDATE_ADMIN_ASSET_REQUEST   = 'UPDATE_ADMIN_ASSET_REQUEST';
export const UPDATE_ADMIN_ASSET_RESPONSE  = 'UPDATE_ADMIN_ASSET_RESPONSE';
export const UPDATE_ADMIN_ASSET_ERROR     = 'UPDATE_ADMIN_ASSET_ERROR';

// view asset audit log ...
export const AUDIT_ADMIN_ASSET            = 'AUDIT_ADMIN_ASSET';

export const ADMIN_ASSET_VEHICLE_CONTROL       = 'ADMIN_ASSET_VEHICLE_CONTROL';
export const ADMIN_ASSET_VEHICLE_CONTROL_SHOW  = 'ADMIN_ASSET_VEHICLE_CONTROL_SHOW';
export const ADMIN_ASSET_VEHICLE_CONTROL_HIDE  = 'ADMIN_ASSET_VEHICLE_CONTROL_HIDE';
export const ADMIN_ASSET_VEHICLE_CONTROL_ERROR = 'ADMIN_ASSET_VEHICLE_CONTROL_ERROR';

// asset delete ...
export const DELETE_ADMIN_ASSET           = 'DELETE_ADMIN_ASSET';
export const DELETE_ADMIN_ASSET_REQUEST   = 'DELETE_ADMIN_ASSET_REQUEST';
export const DELETE_ADMIN_ASSET_RESPONSE  = 'DELETE_ADMIN_ASSET_RESPONSE';
export const DELETE_ADMIN_ASSET_ERROR     = 'DELETE_ADMIN_ASSET_ERROR';

// bulk asset edits ...
export const EDIT_ADMIN_ASSETS            = 'EDIT_ADMIN_ASSETS';
export const EDIT_ADMIN_ASSETS_REQUEST    = 'EDIT_ADMIN_ASSETS_REQUEST';
export const EDIT_ADMIN_ASSETS_RESPONSE   = 'EDIT_ADMIN_ASSETS_RESPONSE';
export const EDIT_ADMIN_ASSETS_ERROR      = 'EDIT_ADMIN_ASSETS_ERROR';

// bulk asset deletes ...
export const DELETE_ADMIN_ASSETS          = 'DELETE_ADMIN_ASSETS';
export const DELETE_ADMIN_ASSETS_REQUEST  = 'DELETE_ADMIN_ASSETS_REQUEST';
export const DELETE_ADMIN_ASSETS_RESPONSE = 'DELETE_ADMIN_ASSETS_RESPONSE';
export const DELETE_ADMIN_ASSETS_ERROR    = 'DELETE_ADMIN_ASSETS_ERROR';

// admin assets filter model ...
export const SET_ADMIN_ASSETS_GRID_FILTER_MODEL = 'SET_ADMIN_ASSETS_GRID_FILTER_MODEL';

// reset last grid action ...
export const RESET_LAST_GRID_ACTION = 'RESET_LAST_GRID_ACTION';

// device move to ...
export const SET_CURRENT_MOVE_TO_ACCOUNT = 'SET_CURRENT_MOVE_TO_ACCOUNT';
/**
 * @initialize
 */
export const initializeAdminAssets = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_ASSETS};
};

export const reInitializeAdminAssets = (): PageInitializeAction => ({
  type : RE_INITIALIZE_ADMIN_ASSETS
});

/**
 * @adminAssetsGridRefreshed
 */
export const adminAssetsGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_ASSETS_GRID_REFRESHED};
};

/**
 * @setCurrentMoveToAccount
 */
export const setCurrentMoveToAccount = (account: Account | Link) => {
  return {type: SET_CURRENT_MOVE_TO_ACCOUNT, account};
};

const deviceStarterTypeList = ['LMU2630', 'LMU1300', 'LMU3640', 'LMU2630'];

/**
 * @getAdminAssets
 *
 * fetches assets for this account to be displayed in the admin
 * section
 */
export const getAdminAssets = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_ASSETS_REQUEST});
    const data: LoadAssetsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {assets: items, totalCount} = await loadAssets(data);
      dispatch({type: GET_ADMIN_ASSETS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_ASSETS_ERROR, error: err});
      return error(err);
      // [ION-SUITE TODO]: display error snackbar if this fails
    }
  };
};

/**
 * Given a Device, moves the device from it's current account to the Asset's account.
 * If the device already resides in the same account as that of the provided Asset,
 * the promise immediately resolves.
 */
export const verifyDevice = async(asset: Asset): Promise<Device | boolean> => {
  const {device} = asset;
  if (device?.account && !hasSameAccount(asset, device)) {
    device.account = asset.account;
    asset.updateAccount = true;
    delete asset.device;
    const updated = await updateDevice(device);
    return updated;
  }
  return Promise.resolve(true);
};

/**
 * Creates the Device associations for a newly created Asset.
 */
export const addAssetDevices = async(asset: Asset, devices?: Link[]): Promise<Asset> => {
  if (devices?.length) {
    const updated = await createAssetDevices(asset.id, devices);
    asset.devices = updated;
  }
  return asset;
};

/**
 * @addAdminAsset
 *
 * Invoked when the Add Admin Asset editor is to be displayed or removed.
 */
export const addAdminAsset = (show = true) => ({
  type : ADD_ADMIN_ASSET, show
});

/**
 * @importAdminAssets
 *
 * Invoked when the Add Admin Asset editor is to be displayed or removed.
 */
export const importAdminAssets = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.asset));
    dispatch({type: IMPORT_ADMIN_ASSETS, show, result});
  };
};

/**
 * @addAsset
 *
 * Invoked when a new asset is to be created; consolidating the process of Asset
 * creation into a single transaction. The process for new Asset creation is as
 * outlined below. Of the 6 potential async calls, 4 are blocking (see awaits).
 *
 * 1.) If a Device is to be associated with the new Asset, and the device does
 *     not reside in the same account as the Asset, the Device will first be
 *     moved. This step is delegated to the verifyDevice function.
 *
 * 2.) The asset's attributes are normalized and then the Asset is created.
 *
 * 3.) If a Device is to be associated with the Asset (see step #1), the newly
 *     created Asset is updated with the Device.
 *     This step is delegated to the addAssetDevices function within this module.
 *
 * 4.) If a Schedule is to be associated with the Asset, the Schedule is added.
 *
 * 5.) The MicroServices are refreshed to inform the API of the changes.
 *
 * 6.) If a Maintenance Schedule is to be associated with the Asset, it is added.
 *
 */
export const addAsset = (asset: Asset): GenericFunction<any> => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_ASSET_REQUEST});
    removeQueries(QueryKey.DEVICE_GET, false);
    const {schedule, maintenanceSchedule} = asset;
    try {
      await verifyDevice(asset);
      const created = await createAsset(normalizeAsset(asset));
      await addAssetDevices(created, asset.devices);
      schedule && await manageAssetSchedules(created, [schedule]);
      maintenanceSchedule && assignSchedule(created, maintenanceSchedule);
      dispatch({type: ADD_ADMIN_ASSET_RESPONSE, asset: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.asset)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_ASSET_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.asset, err)));
      error(err);
    }
  };
};

/**
 * @editAdminAsset
 *
 * Invoked when the Edit Admin Asset editor is to be displayed or removed.
 */
export const editAdminAsset = (asset: Asset, show = true) => ({
  type : EDIT_ADMIN_ASSET, asset, show
});

/**
 * @editAsset
 *
 * Invoked when an existing asset is to be updated; consolidating the process of
 * Asset updates into a single transaction. The process for new Asset creation is
 * outlined below.
 *
 * 1.) This step is delegated to the consolidatedAssetUpdate function of the connect
 *     services module, where the majority of complexity is managed, such as device
 *     associations, updating the asset, etc.
 *
 * 2.) If the Asset had a Maintenance Schedule, it will be removed.
 *
 * 3.) If the Asset has a Maintenance Schedule, it will be assigned.
 *
 */
export const editAsset = (asset: Asset, messageEntry?: string, successCallback?: Function): GenericFunction<any> => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSET_REQUEST});
    removeQueries(QueryKey.DEVICE_GET, false);
    const {previousAccount, maintenanceSchedule, maintenanceSchedules, schedule, id, account, devices} = asset;
    const executions = [];
    try {
      if (!maintenanceSchedule && maintenanceSchedules) {
        executions.unshift(deleteAssetMaintenanceSchedule(id));
      }
      const updated = await consolidatedAssetUpdate(asset);
      await updateAssetSchedule(asset);
      maintenanceSchedule && assignSchedule(asset, maintenanceSchedule);
      successCallback?.();
      dispatch({type: EDIT_ADMIN_ASSET_RESPONSE, asset: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.asset, null, messageEntry)));
    } catch (err) {
      // best-effort revert of any Account/Device move consolidatedAssetUpdate
      // may have partially applied before failing; its own failure must never
      // prevent the error snackbar or the loading state below from resetting,
      // such as when the original failure was a field validation error and
      // this retry is rejected for the same reason ...
      try {
        const updated = await getAsset(asset.id);
        await updateAsset({...asset, version: updated.version, devices, previousAccount});
      } catch (revertErr) {
        error(revertErr);
      }
      dispatch(toggleUserPrompt(errorEditOptions(Entity.asset, err)));
      dispatch({type: EDIT_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @manageAdminAsset
 *
 * Invoked when the Manage Admin Asset Associations editor is to be displayed
 * or removed.
 */
export const manageAdminAsset = (asset: Asset, show = true) => ({
  type : MANAGE_ADMIN_ASSET, asset, show
});

/**
 * @manageAsset
 *
 * Invoked when existing asset associated are to be updated, delegating to the updateAsset
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const manageAsset = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MANAGE_ADMIN_ASSET_REQUEST});
    try {
      const updated = await updateAsset(asset);
      dispatch({type: MANAGE_ADMIN_ASSET_RESPONSE, asset: updated});
    } catch (err) {
      dispatch({type: MANAGE_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @updateAdminAsset
 *
 * Invoked when the Update Admin Asset Associations editor is to be displayed
 * or removed.
 */
export const updateAdminAsset = (asset: Asset, assignment: String, show = true) => ({
  type : UPDATE_ADMIN_ASSET, asset, show, assignment
});

/**
 * @updateAsset
 *
 * Invoked when existing asset associated are to be updated, delegating to the
 * updateAssetToolAssociations CTC API and dispatching a
 *  successful response or error action accordingly. Replaces
 * the set of assets currently associated
 * with the asset, with the supplied set of assets.
 */
export const updateAssetAssociations = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_REQUEST});
    try {
      const updated = await updateAssetToolAssociations(asset);
      dispatch({type: UPDATE_ADMIN_ASSET_RESPONSE, asset: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.asset)));
    } catch (err) {
      dispatch({type: UPDATE_ADMIN_ASSET_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.asset, err)));
      error(err);
    }
  };
};

/**
 * @manageAssignmentsRefresh
 *
 * Invoked when after a successful update of assets and/or operators
 * from the AssignmentEditor
 */
export const manageAssignmentsRefresh = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_RESPONSE, asset});
    dispatch(toggleUserPrompt(successEditOptions(Entity.asset)));
  };
};

/**
 * @manageAssignmentsError
 *
 * Invoked after an unsuccessful attempt to update assets and/or operators
 * from the AssignmentEditor
 */
export const manageAssignmentsError = (err) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_ERROR, error: err});
    dispatch(toggleUserPrompt(errorEditOptions(Entity.asset, err)));
    error(err);
  };
};

/**
 * @auditAdminAsset
 *
 * Invoked when the the asset auditor is to be displayed or removed.
 */
export const auditAdminAsset = (asset: Asset, show = true) => ({
  type : AUDIT_ADMIN_ASSET, asset, show
});

/**
 * @adminAssetVehicleControl
 *
 * Invoked when the asset Vehicle Control message is to be displayed.
 */
export const adminAssetVehicleControl = (asset: Asset) => ({
  type : ADMIN_ASSET_VEHICLE_CONTROL_SHOW, asset
});

/**
 * @adminAssetVehicleControlError
 *
 * Invoked when the asset Vehicle Control error is to be displayed.
 */
export const adminAssetVehicleControlError = () => ({
  type : ADMIN_ASSET_VEHICLE_CONTROL_ERROR
});

/**
 * @adminAssetVehicleControlHide
 *
 * Invoked when the the asset Vehicle Control message is to be removed.
 */
export const adminAssetVehicleControlHide = () => ({
  type : ADMIN_ASSET_VEHICLE_CONTROL_HIDE
});

/**
 * @enableVehicleControlStatus
 *
 * Invoked when asset's vehicle control status needs to be enabled.
 */
export const enableVehicleControlStatus = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADMIN_ASSET_VEHICLE_CONTROL, asset: null});
    try {
      let extAttrib: GenericObject  = {};
      const updatedAsset = {...asset};
      extAttrib = safeJSONParse(updatedAsset?.extendedAttribute?.value, {});
      extAttrib.engineLockPending = false;
      Object.assign(updatedAsset, {
        extendedAttribute : {value: JSON.stringify(extAttrib)}
      });
      const id = entityId(asset.devices[0].href);
      await updateAsset(updatedAsset);
      await sendStarterCommand(id, true);
    } catch (err) {
      error(err);
    }
  };
};

/**
 * @changeVehicleControlStatus
 *
 * Invoked when asset's vehicle control status needs to be disabled.
 */
export const disableVehicleControlStatus = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADMIN_ASSET_VEHICLE_CONTROL, asset: null});
    try {
      const id = entityId(asset.devices[0].href);
      await sendStarterCommand(id, false);
    } catch (err) {
      error(err);
    }
  };
};

/**
 * @deleteAdminAsset
 *
 * Invoked when the Delete Admin Asset Dialog is to be displayed or removed.
 */
export const deleteAdminAsset = (asset: Asset, show = true) => ({
  type : DELETE_ADMIN_ASSET, asset, show
});

/**
 * @deleteAsset
 *
 * Invoked when an existing asset is to be deleted, delegating to the deleteAsset
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteAsset = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ASSET_REQUEST});
    const {
      account,
      devices,
      id: assetId,
      schedules,
      maintenanceSchedules
    } = asset;
    const executions = [];
    if (maintenanceSchedules) {
      executions.unshift(deleteAssetMaintenanceSchedule(assetId));
    }
    try {
      if (devices?.length) {
        executions.unshift(disassociateDevices(asset));
      }
      if (schedules) {
        // delete schedule which is directly associated with the
        // asset before deleting it.
        await deleteAssetSchedule(asset);
      }
      // Wait for all cleanups to finish
      await Promise.all(executions);

      // Now safe to delete the asset
      const deleted = await destroyAsset(asset);
      dispatch({type: DELETE_ADMIN_ASSET_RESPONSE, asset: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @editAdminAssets
 *
 * Invoked when the Bulk Edit Admin Asset editor is to be displayed or removed.
 */
export const editAdminAssets = (assets: Asset[], show = true) => ({
  type : EDIT_ADMIN_ASSETS, assets, show
});

/**
 * @editAssets
 *
 * Invoked when a collection of existing assets are to be updated, delegating to
 * the updateAssets CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editAssets = (assets: Asset[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSETS_REQUEST});
    removeQueries(QueryKey.DEVICE_GET, false);
    try {
      const {results} = await updateAssetSchedules(assets);
      const scheduleMap = {};
      const maintenanceScheduleMap = {};
      assets?.forEach(asset => {
        scheduleMap[asset.name] = asset?.schedule || null;
        maintenanceScheduleMap[asset.name] = asset?.maintenanceSchedule || null;
      });
      await updateBulkAssetSchedules(assets);
      updateBulkAssetMaintenanceSchedules(assets, maintenanceScheduleMap);
      dispatch(toggleUserPrompt(successEditOptions(translate('common:entityLabels.assets'),
        {results, items: assets})));
      dispatch({type: EDIT_ADMIN_ASSETS_RESPONSE, assets: results});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_ASSETS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminAssets
 *
 * Invoked when the Delete Admin Assets Dialog is to be displayed or removed.
 */
export const deleteAdminAssets = (assets: Asset[], show = true) => ({
  type : DELETE_ADMIN_ASSETS, assets, show
});

/**
 * @deleteAssets
 *
 * Invoked when a collection of existing assets are to be updated, delegating to
 * the updateAssets CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteAssets = (assets: Asset[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ASSETS_REQUEST});
    try {
      await deleteAssetDevices(assets);
      const deleted = await destroyAssets(assets, assets?.length);
      dispatch({type: DELETE_ADMIN_ASSETS_RESPONSE, assets: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ASSETS_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_ASSETS_GRID_FILTER_MODEL, model
});
